import { trackVideoEvent } from './VideoPlayerAnalytics';
import {
  customErrorEvents,
  videoPlaybackEvents,
  videoTrackingEvents,
} from './VideoPlayerConstants';

const {
  BUFFER,
  CUEPOINT,
  ERROR,
  FINISH,
  HLS_ERROR,
  HLS_LEVEL_SWITCH,
  PAUSE,
  PROGRESS,
  READY,
  RESUME,
  RESUME_SEEK,
} = videoPlaybackEvents;

const analyticsEventMap = {};
analyticsEventMap[ERROR] = videoTrackingEvents.ERROR;
analyticsEventMap[HLS_ERROR] = videoTrackingEvents.INTERRUPTED;
analyticsEventMap[HLS_LEVEL_SWITCH] = videoTrackingEvents.QUALITY_UPDATED;
analyticsEventMap[FINISH] = videoTrackingEvents.COMPLETED;
analyticsEventMap[PAUSE] = videoTrackingEvents.PAUSED;
analyticsEventMap[RESUME] = videoTrackingEvents.RESUMED;

const trackFlowPlayerEvent = (flowPlayerEvent, player, trackData = null, error = null) => {
  const analyticsEvent = analyticsEventMap[flowPlayerEvent];
  if (analyticsEvent) {
    trackVideoEvent(analyticsEvent, player, trackData, error);
  }
};

const getPercentageTracking = () => ({
  0: false,
  25: false,
  50: false,
  75: false,
  100: false,
});

export { analyticsEventMap, getPercentageTracking, trackFlowPlayerEvent };
export default class VideoPlayerEvents {
  constructor(player, callback, trackData) {
    this.player = player;
    this.callback = callback;
    this.trackData = trackData;
    this.percentageTracked = null;
    this.paywallCTA = false;
    this.started = false;
  }

  onCommonEvent = event => {
    const { type } = event || {};
    trackFlowPlayerEvent(type, this.player, this.trackData);
    this.callback(type);
  };

  onCuePointEvent = () => {
    const timeToSecond = Math.round(this.player.video.time);
    this.callback(CUEPOINT, timeToSecond);
    trackVideoEvent(videoTrackingEvents.CUEPOINT, this.player, this.trackData);
  };

  onErrorEvent = (event, data, error) => {
    const { type } = event || {};
    trackFlowPlayerEvent(type, this.player, this.trackData, error);
    if (error && error.code) {
      const { code } = error;
      switch (code) {
        case 10:
          this.callback(ERROR, customErrorEvents.BROWSER_UNSUPPORTED);
          break;
        default:
          this.callback(ERROR, customErrorEvents.VIDEO_UNAVAILABLE);
          break;
      }
    }
  };

  onFinishEvent = () => {
    this.player.on(RESUME_SEEK, () => {
      this.callback(FINISH, false);
      trackVideoEvent(videoTrackingEvents.SEEK_COMPLETED, this.player, this.trackData);
    });
    this.callback(FINISH, true);
    trackVideoEvent(videoTrackingEvents.COMPLETED, this.player, this.trackData);
  };

  onProgressEvent = () => {
    const { video } = this.player || {};
    const { duration, time } = video || {};
    if (time !== undefined && time > -1) {
      this.parseProgressEvent(time, duration);
    }
  };

  onReadyEvent = () => {
    this.player.on(RESUME, () => {
      trackVideoEvent(videoTrackingEvents.RESUMED, this.player, this.trackData);
      if (!this.started) {
        this.callback('started');
      } else {
        this.callback(PAUSE, false);
      }
    });
  };

  parseProgressEvent = (time, duration) => {
    const timeIntervals = [20, 30];
    const timeToSecond = Math.round(time);
    if (timeToSecond !== -1 && timeToSecond % 10 === 0) {
      this.callback('10s', this.player);
    }
    const percentage = Math.round((time / duration) * 100);
    if (percentage % 25 === 0 && !this.percentageTracked[percentage]) {
      this.callback('watching', percentage, timeToSecond);
      this.percentageTracked[percentage] = true;
    } else if (timeIntervals.includes(timeToSecond)) {
      this.callback('watching', percentage, timeToSecond);
    }

    if (timeToSecond === 5 && !this.paywallCTA) {
      this.paywallCTA = true;
      this.callback('paywallCTA');
    }
  };

  register() {
    this.percentageTracked = getPercentageTracking();
    this.player.one(READY, this.onReadyEvent); // one is an event called once
    this.player.on(CUEPOINT, this.onCuePointEvent);
    this.player.on(FINISH, this.onFinishEvent);
    this.player.on(PROGRESS, this.onProgressEvent);
    this.registerCommonEvents();
    this.registerErrorHandling();
  }

  unregister() {
    Object.keys(videoPlaybackEvents).forEach(event => {
      this.player.off(videoPlaybackEvents[event]);
    });
  }

  registerCommonEvents() {
    [BUFFER, HLS_LEVEL_SWITCH, PAUSE].forEach(event => {
      this.player.on(event, this.onCommonEvent);
    });
  }

  registerErrorHandling() {
    [ERROR, HLS_ERROR].forEach(eventType => {
      this.player.on(eventType, this.onErrorEvent);
    });
  }
}
